import {useCallback} from 'react';

import {useRestMutation} from 'web-app/react/hooks/use-rest-mutation';
import type {
    CreateBillingPlanMutation,
    CreateBillingPlanMutationVariables,
} from 'web-app/api/clients/rest-types/create-billing-plan/create-billing-plan';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as Warnable from 'web-app/util/warnable';
import * as SentryService from 'web-app/services/sentry';

import {CreateBillingPlan} from '../queries/create-billing-plan.rest-mutation';
import type * as Types from '../types';
import {useSubmitTuition} from './use-submit-tuition';
import type {SubmissionData} from './use-submission-data';
import {transformApiSessionToTuition} from '../transformers/api-transformers';

export const useCreatePlansForChildren = () => {
    const [createBillingPlan] = useRestMutation<CreateBillingPlanMutation, CreateBillingPlanMutationVariables>(
        CreateBillingPlan,
    );
    const {submit: createTuition} = useSubmitTuition();

    const createTuitionsWithOverriddenPrice = useCallback(
        (tuitions: SubmissionData['tuitionsWithOverriddenPrice'], billingProfiles: Types.BillingProfile[]) => {
            if (!hasValue(tuitions)) {
                return null;
            }

            return tuitions.overriddenPriceTuitions
                .map(async tuition => {
                    const createdTuition = await createTuition(tuition.newTuition);
                    if (!createdTuition.data) {
                        return null;
                    }

                    const newTuitionWithWarning = transformApiSessionToTuition({
                        billingProfiles,
                        hiddenTuitionsToDisplay: [createdTuition.data.createSessionPackage.session.id],
                    })(createdTuition.data.createSessionPackage.session);

                    const newTuition = Warnable.dispatchWarnings(newTuitionWithWarning, warnings => {
                        SentryService.captureMessage(
                            `Tuitions with overridden prices warnings: ${warnings.map(w => w.warning).join(', ')}`,
                        );
                    });

                    if (!hasValue(newTuition)) {
                        return null;
                    }

                    return {
                        newTuition,
                        oldTuitionId: tuition.oldTuitionId,
                    };
                })
                .filter(hasValue);
        },
        [createTuition],
    );

    const tuitionsForPlans = useCallback(
        async (
            selectedTuitions: Types.Tuition[],
            tuitionsWithOverriddenPricePromise:
                | Promise<{
                      newTuition: Types.Tuition;
                      oldTuitionId: string;
                  } | null>[]
                | null,
        ) => {
            if (!hasValue(tuitionsWithOverriddenPricePromise)) {
                return selectedTuitions;
            }
            const tuitionsWithOverriddenPrice = await Promise.all(tuitionsWithOverriddenPricePromise);
            if (hasValue(tuitionsWithOverriddenPrice)) {
                const tuitionsNonEmpty = tuitionsWithOverriddenPrice.filter(hasValue);
                const tuitionsForPlans = [
                    ...tuitionsNonEmpty.map(tuition => tuition.newTuition),
                    ...selectedTuitions.filter(
                        tuition =>
                            !tuitionsNonEmpty.some(
                                tuitionWithOverriddenPrice => tuitionWithOverriddenPrice.oldTuitionId === tuition.id,
                            ),
                    ),
                ];
                return tuitionsForPlans;
            }

            return selectedTuitions;
        },
        [],
    );

    const createPlansForChildren = useCallback(
        async ({
            tuitionsWithOverriddenPrice,
            billingProfiles,
            selectedTuitions,
            plans,
        }: {
            tuitionsWithOverriddenPrice: SubmissionData['tuitionsWithOverriddenPrice'];
            billingProfiles: Types.BillingProfile[];
            selectedTuitions: Types.Tuition[];
            plans: SubmissionData['plans'];
        }) => {
            const tuitionsWithOverriddenPricePromise = hasValue(tuitionsWithOverriddenPrice)
                ? createTuitionsWithOverriddenPrice(tuitionsWithOverriddenPrice, billingProfiles)
                : null;

            const tuitions = await tuitionsForPlans(selectedTuitions, tuitionsWithOverriddenPricePromise);

            const planPromises = plans({
                selectedTuitions: tuitions,
            }).map(plan => {
                return createBillingPlan({variables: plan});
            });

            return Promise.all(planPromises);
        },
        [createBillingPlan, createTuitionsWithOverriddenPrice, tuitionsForPlans],
    );

    return {createPlansForChildren};
};
